import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Store,
  ShoppingBag,
  LogOut,
  LayoutDashboard,
  MapPin,
  Settings,
  Users,
  Menu,
  Home,
  User,
  Package,
  Users as CustomersIcon,
  BarChart3,
  Shield,
  Building,
  RefreshCw,
  Truck,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  getCurrentShop,
  getCurrentUser,
  logout as performLogout,
  hasPermission,
  getCurrentOwner,
  switchToOwnerMode,
  clearCurrentShop,
  clearCurrentUser,
  isMultiTenantOwner,
} from "@/lib/auth";
import { Bell, X, CheckCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  getShopNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from "@/lib/api";

export function Navbar() {
  const [location, setLocation] = useLocation();

  // ========== NO MOSTRAR NAVBAR EN PÁGINA DE SUSPENSIÓN ==========
  if (location === "/shop/suspended") {
    return null;
  }

  const currentShop = getCurrentShop();
  const currentUser = getCurrentUser();

  // Estados para notificaciones
  const [notifications, setNotifications] = useState<any[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Cargar notificaciones en Navbar
  useEffect(() => {
    // Si no hay currentShop, no hacer nada
    if (!currentShop) return;

    let isMounted = true;

    const loadNotifications = async () => {
      if (!isMounted) return;

      try {
        const notifs = await getShopNotifications(currentShop.id);

        if (isMounted) {
          setNotifications(notifs);
          const unread = notifs.filter((n) => !n.isRead).length;
          setUnreadCount(unread);
        }
      } catch (error) {
        console.error("Error cargando notificaciones en Navbar:", error);
      }
    };

    loadNotifications();

    // Refrescar cada 60 segundos
    const interval = setInterval(loadNotifications, 60000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [currentShop?.id]);

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marcando notificación como leída:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!currentShop) return;
    try {
      await markAllNotificationsAsRead(currentShop.id);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marcando todas como leídas:", error);
    }
  };

  const handleLogout = () => {
    performLogout();
    setLocation("/shop/login");
  };

  return (
    <nav className="border-b bg-card sticky top-0 z-40 shadow-sm">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Link href="/">
          <span className="flex items-center gap-2 font-heading font-bold text-xl text-primary uppercase tracking-wide cursor-pointer">
            <MapPin className="h-6 w-6" />
            QvaPOS
          </span>
        </Link>

        <div className="flex items-center gap-4">
          {currentShop ? (
            <>
              {/* Desktop Navigation - hidden en mobile */}
              <div className="hidden md:flex items-center gap-6 mr-4 text-sm font-medium">
                {/* Dashboard - Ahora también visible para cashier */}
                {(currentUser?.role === "supervisor" ||
                  currentUser?.role === "store_manager" ||
                  currentUser?.role === "cashier") && (
                  <NavLink
                    href="/shop/dashboard"
                    icon={<LayoutDashboard className="h-4 w-4" />}
                    text="Dashboard"
                  />
                )}
                <NavLink
                  href="/shop/pos"
                  icon={<ShoppingBag className="h-4 w-4" />}
                  text="POS"
                />

                {/* === MENÚ DESPLEGABLE: GESTIÓN DE INVENTARIO === */}
                {(currentUser?.role === "supervisor" ||
                  currentUser?.role === "store_manager" ||
                  currentUser?.role === "cashier") && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <span className="flex items-center gap-2 transition-colors hover:text-primary cursor-pointer text-muted-foreground">
                        <Package className="h-4 w-4" />
                        Inventario
                        {/* Flecha indicadora */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="ml-1 opacity-70"
                        >
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </span>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-[220px]">
                      {/* Catálogo de Productos (renombre del inventario actual) */}
                      <DropdownMenuItem asChild>
                        <Link
                          href="/shop/inventory"
                          className="cursor-pointer flex items-center gap-2"
                        >
                          <Package className="h-4 w-4" />
                          <span>Catálogo de Productos</span>
                        </Link>
                      </DropdownMenuItem>
                      {/* Entradas/Salidas */}
                      <DropdownMenuItem asChild>
                        <Link
                          href="/shop/inventory/movements"
                          className="cursor-pointer flex items-center gap-2"
                        >
                          <RefreshCw className="h-4 w-4" />
                          <span>Entradas/Salidas</span>
                        </Link>
                      </DropdownMenuItem>
                      {/* Transferencias */}
                      <DropdownMenuItem asChild>
                        <Link
                          href="/shop/inventory/transfers"
                          className="cursor-pointer flex items-center gap-2"
                        >
                          <Truck className="h-4 w-4" />
                          <span>Transferencias</span>
                        </Link>
                      </DropdownMenuItem>
                      {/* Proveedores */}
                      <DropdownMenuItem asChild>
                        <Link
                          href="/shop/inventory/suppliers"
                          className="cursor-pointer flex items-center gap-2"
                        >
                          <Building className="h-4 w-4" />
                          <span>Proveedores</span>
                        </Link>
                      </DropdownMenuItem>
                      {/* Análisis y Reportes */}
                      <DropdownMenuItem asChild>
                        <Link
                          href="/shop/inventory/analytics"
                          className="cursor-pointer flex items-center gap-2"
                        >
                          <BarChart3 className="h-4 w-4" />
                          <span>Análisis y Reportes</span>
                        </Link>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {/* === FIN MENÚ DESPLEGABLE === */}
                {/* Solo mostrar Usuarios si tiene permiso */}
                {(currentUser?.role === "supervisor" ||
                  currentUser?.role === "store_manager") && (
                  <NavLink
                    href="/shop/users"
                    icon={<Users className="h-4 w-4" />}
                    text="Usuarios"
                  />
                )}

                {/* Solo cashier, manager y admin ven Clientes */}
                {(currentUser?.role === "supervisor" ||
                  currentUser?.role === "store_manager" ||
                  currentUser?.role === "cashier") && (
                  <NavLink
                    href="/shop/customers"
                    icon={<CustomersIcon className="h-4 w-4" />}
                    text="Clientes"
                  />
                )}

                {/* Solo admin y manager ven Reportes */}
                {(currentUser?.role === "supervisor" ||
                  currentUser?.role === "store_manager") && (
                  <NavLink
                    href="/shop/reports"
                    icon={<BarChart3 className="h-4 w-4" />}
                    text="Reportes"
                  />
                )}

                {/* ========== NUEVO ENLACE A DEVOLUCIONES ========== */}
                {(currentUser?.role === "supervisor" ||
                  currentUser?.role === "store_manager") && (
                  <NavLink
                    href="/shop/returns"
                    icon={<RefreshCw className="h-4 w-4" />}
                    text="Devoluciones"
                  />
                )}

                {/* Solo admin ve Configuración */}
                {currentUser?.role === "supervisor" && (
                  <NavLink
                    href="/shop/settings"
                    icon={<Settings className="h-4 w-4" />}
                    text="Configuración"
                  />
                )}
              </div>

              {/* Mobile Menu - Hamburguesa */}
              <div className="md:hidden">
                <MobileMenu
                  currentShop={currentShop}
                  currentUser={currentUser}
                  handleLogout={handleLogout}
                />
              </div>

              {/* User info y logout button (siempre visible) */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold hidden sm:inline-block text-foreground">
                  {currentShop.name}
                </span>{" "}
                {/* Botón "Volver a Modo Empresa" - Solo para owners (ID empieza con shop_) */}
                {currentUser?.id?.startsWith("shop_") && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      clearCurrentShop();
                      clearCurrentUser();
                      window.location.href = "/owner/dashboard";
                    }}
                    className="hidden sm:flex items-center gap-1"
                  >
                    <Building className="h-3 w-3" />
                    <span className="hidden md:inline">Modo Empresa</span>
                  </Button>
                )}
                {currentUser?.role === "supervisor" && !currentUser?.id?.startsWith("shop_") && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      clearCurrentShop();
                      window.location.href = "/supervisor/dashboard";
                    }}
                    className="hidden sm:flex items-center gap-1"
                  >
                    <Building className="h-3 w-3" />
                    <span className="hidden md:inline">Panel Supervisor</span>
                  </Button>
                )}
                {currentUser && (
                  <span className="hidden sm:inline text-sm font-medium text-foreground px-2 py-1 ml-2">
                    {currentUser.fullName || currentUser.username || "Usuario"}
                  </span>
                )}
                {/* Botón de notificaciones en navbar */}
                <div className="relative">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="relative"
                  >
                    <Bell className="h-4 w-4" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </Button>

                  {/* Panel de notificaciones */}
                  {showNotifications && (
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-50 max-h-96 overflow-y-auto">
                      <div className="p-4 border-b flex justify-between items-center">
                        <h3 className="font-semibold">Notificaciones</h3>
                        <div className="flex gap-2">
                          {unreadCount > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                handleMarkAllAsRead();
                                setShowNotifications(false);
                              }}
                              className="h-8 text-xs"
                            >
                              <CheckCheck className="h-3 w-3 mr-1" />
                              Marcar todas
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowNotifications(false)}
                            className="h-8"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {notifications.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                          <Bell className="h-12 w-12 mx-auto mb-4 opacity-20" />
                          <p>No hay notificaciones</p>
                        </div>
                      ) : (
                        <div className="divide-y">
                          {notifications.map((notification) => (
                            <div
                              key={notification.id}
                              className={`p-4 hover:bg-gray-50 ${!notification.isRead ? "bg-blue-50" : ""}`}
                              onClick={() =>
                                !notification.isRead &&
                                handleMarkAsRead(notification.id)
                              }
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                                        notification.type === "warning"
                                          ? "bg-amber-100 text-amber-800"
                                          : notification.type === "error"
                                            ? "bg-red-100 text-red-800"
                                            : notification.type === "payment"
                                              ? "bg-green-100 text-green-800"
                                              : "bg-blue-100 text-blue-800"
                                      }`}
                                    >
                                      {notification.type === "warning"
                                        ? "⚠️"
                                        : notification.type === "error"
                                          ? "🔴"
                                          : notification.type === "payment"
                                            ? "💰"
                                            : "ℹ️"}
                                      {notification.type}
                                    </span>
                                    {notification.createdByAdmin && (
                                      <span className="text-xs text-muted-foreground">
                                        Admin
                                      </span>
                                    )}
                                  </div>
                                  <h4 className="font-semibold text-sm">
                                    {notification.title}
                                  </h4>
                                  <p className="text-sm text-gray-600 mt-1">
                                    {notification.message}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-2">
                                    {new Date(
                                      notification.createdAt,
                                    ).toLocaleString()}
                                  </p>
                                </div>
                                {!notification.isRead && (
                                  <Badge
                                    className="ml-2 h-6"
                                    variant="secondary"
                                  >
                                    Nuevo
                                  </Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  data-testid="button-logout"
                  className="hidden sm:flex"
                >
                  <LogOut className="h-4 w-4 md:mr-2" />
                  <span className="hidden md:inline">Salir</span>
                </Button>
              </div>
            </>
          ) : // Mostrar Navbar para owner cuando está en /owner/dashboard
          location === "/owner/dashboard" ? (
            <>
              {/* Desktop Navigation para Owner */}
              <div className="hidden md:flex items-center gap-6 mr-4 text-sm font-medium">
                <NavLink
                  href="/owner/dashboard"
                  icon={<LayoutDashboard className="h-4 w-4" />}
                  text="Dashboard"
                />
                <NavLink
                  href="/owner/discrepancies"
                  icon={<BarChart3 className="h-4 w-4" />}
                  text="Discrepancias"
                />
                <NavLink
                  href="/owner/plans"
                  icon={<Shield className="h-4 w-4" />}
                  text="Planes"
                />
              </div>

              {/* Mobile Menu para Owner */}
              <div className="md:hidden">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" size="icon">
                      <Menu className="h-5 w-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[280px]">
                        <div className="mt-6 max-h-[calc(100dvh-5rem)] overflow-y-auto">
                          <div className="mb-6 px-2">
                            <div className="flex items-center gap-2 mb-4">
                              <MapPin className="h-6 w-6 text-primary" />
                              <span className="font-heading font-bold text-lg">
                                QvaPOS
                              </span>
                            </div>
                            {getCurrentOwner() && (
                          <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                            <div className="text-sm font-bold">
                              {getCurrentOwner()?.businessName}
                            </div>
                            <div className="text-sm font-medium text-foreground mt-1">
                              {getCurrentOwner()?.ownerName}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              Dueño
                            </div>
                          </div>
                        )}
                      </div>
                      <nav className="flex flex-col gap-1">
                        <MobileNavLink
                          href="/owner/dashboard"
                          icon={<LayoutDashboard className="h-5 w-5" />}
                          text="Dashboard"
                          onClick={() => {}}
                        />
                        <MobileNavLink
                          href="/owner/discrepancies"
                          icon={<BarChart3 className="h-5 w-5" />}
                          text="Discrepancias"
                          onClick={() => {}}
                        />
                        <MobileNavLink
                          href="/owner/plans"
                          icon={<Shield className="h-5 w-5" />}
                          text="Planes"
                          onClick={() => {}}
                        />
                        <div className="border-t my-3 pt-3">
                          <Button
                            variant="ghost"
                            className="w-full justify-start gap-3 h-12 text-destructive hover:text-destructive"
                            onClick={() => {
                              handleLogout();
                            }}
                          >
                            <LogOut className="h-5 w-5" />
                            <span className="text-base">Cerrar Sesión</span>
                          </Button>
                        </div>
                      </nav>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>

              {/* User info y logout para Owner */}
              <div className="flex items-center gap-2">
                {getCurrentOwner() && (
                  <span className="hidden sm:inline text-sm font-medium text-foreground px-2 py-1">
                    {getCurrentOwner()?.ownerName}
                  </span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className="hidden sm:flex"
                >
                  <LogOut className="h-4 w-4 md:mr-2" />
                  <span className="hidden md:inline">Salir</span>
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* No logueado - Desktop */}
              <div className="hidden md:flex items-center gap-4">
                <Link href="/home">
                  <Button variant="ghost" size="sm">
                    <Home className="h-4 w-4 mr-2" />
                    Inicio
                  </Button>
                </Link>
                <Link href="/shop/login">
                  <Button variant="ghost" size="sm">
                    <User className="h-4 w-4 mr-2" />
                    Ingresar
                  </Button>
                </Link>
                <Link href="/admin/login"></Link>
                <Link href="/register">
                  <Button
                    variant="default"
                    size="sm"
                    className="font-bold uppercase tracking-wider"
                  >
                    <Store className="h-4 w-4 mr-2" />
                    Soy Vendedor
                  </Button>
                </Link>
              </div>

              {/* No logueado - Mobile */}
              <div className="md:hidden">
                <MobileMenu
                  currentShop={null}
                  currentUser={null}
                  handleLogout={handleLogout}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

// === COMPONENTES AUXILIARES (FUERA del componente Navbar) ===

// Componente para enlaces de navegación
function NavLink({
  href,
  icon,
  text,
}: {
  href: string;
  icon: React.ReactNode;
  text: string;
}) {
  const [location] = useLocation();

  return (
    <Link href={href}>
      <span
        className={`flex items-center gap-2 transition-colors hover:text-primary cursor-pointer ${location === href ? "text-primary" : "text-muted-foreground"}`}
      >
        {icon}
        {text}
      </span>
    </Link>
  );
}

// Componente del menú móvil
function MobileMenu({
  currentShop,
  currentUser,
  handleLogout,
}: {
  currentShop: any;
  currentUser: any;
  handleLogout: () => void;
}) {
  const [open, setOpen] = useState(false);

  const handleNavigation = () => {
    setOpen(false); // Cerrar el menú al hacer clic en un enlace
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon">
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[280px]">
        <div className="mt-6 max-h-[calc(100dvh-5rem)] overflow-y-auto">
          {/* Logo y info */}
          <div className="mb-6 px-2">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-6 w-6 text-primary" />
              <span className="font-heading font-bold text-lg">QvaPOS</span>
            </div>
            {currentShop && (
              <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                <div className="text-sm font-bold">{currentShop.name}</div>
                {currentUser && (
                  <>
                    <div className="text-sm font-medium text-foreground mt-1">
                      {currentUser.fullName ||
                        currentUser.username ||
                        "Usuario"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {currentUser.role === "supervisor"
                        ? "Supervisor"
                        : currentUser.role === "store_manager"
                          ? "Gerente"
                          : "Vendedor"}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Menú de navegación */}
          <nav className="flex flex-col gap-1">
            {currentShop ? (
              // Usuario logueado
              <>
                {/* Dashboard - Ahora también visible para cashier */}
                {(currentUser?.role === "supervisor" ||
                  currentUser?.role === "store_manager" ||
                  currentUser?.role === "cashier") && (
                  <MobileNavLink
                    href="/shop/dashboard"
                    icon={<LayoutDashboard className="h-5 w-5" />}
                    text="Dashboard"
                    onClick={handleNavigation}
                  />
                )}
                <MobileNavLink
                  href="/shop/pos"
                  icon={<ShoppingBag className="h-5 w-5" />}
                  text="Punto de Venta"
                  onClick={handleNavigation}
                />
                {/* === SUBMENÚ MÓVIL: GESTIÓN DE INVENTARIO === */}
                {(currentUser?.role === "supervisor" ||
                  currentUser?.role === "store_manager" ||
                  currentUser?.role === "cashier") && (
                  <Collapsible className="my-1">
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" className="w-full justify-start gap-3 h-12 px-4">
                        <Package className="h-5 w-5 shrink-0" />
                        <ChevronRight className="h-5 w-5 transition-transform data-[state=open]:rotate-90 shrink-0" />
                        <span className="text-base font-normal">Inventario</span>
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="pl-4 border-l-2 border-muted/30 ml-4">
                      <MobileNavLink
                        href="/shop/inventory"
                        icon={<Package className="h-4 w-4" />}
                        text="Catálogo de Productos"
                        onClick={handleNavigation}
                      />
                      <MobileNavLink
                        href="/shop/inventory/movements"
                        icon={<RefreshCw className="h-4 w-4" />}
                        text="Entradas/Salidas"
                        onClick={handleNavigation}
                      />
                      <MobileNavLink
                        href="/shop/inventory/transfers"
                        icon={<Truck className="h-4 w-4" />}
                        text="Transferencias"
                        onClick={handleNavigation}
                      />
                      <MobileNavLink
                        href="/shop/inventory/suppliers"
                        icon={<Building className="h-4 w-4" />}
                        text="Proveedores"
                        onClick={handleNavigation}
                      />
                      <MobileNavLink
                        href="/shop/inventory/analytics"
                        icon={<BarChart3 className="h-4 w-4" />}
                        text="Análisis y Reportes"
                        onClick={handleNavigation}
                      />
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
                {/* === FIN SUBMENÚ MÓVIL === */}
                {/* Solo mostrar Usuarios si tiene permiso */}
                {(currentUser?.role === "supervisor" ||
                  currentUser?.role === "store_manager") && (
                  <MobileNavLink
                    href="/shop/users"
                    icon={<Users className="h-5 w-5" />}
                    text="Usuarios"
                    onClick={handleNavigation}
                  />
                )}
                {/* Solo cashier, manager y admin ven Clientes */}
                {(currentUser?.role === "supervisor" ||
                  currentUser?.role === "store_manager" ||
                  currentUser?.role === "cashier") && (
                  <MobileNavLink
                    href="/shop/customers"
                    icon={<CustomersIcon className="h-5 w-5" />}
                    text="Clientes"
                    onClick={handleNavigation}
                  />
                )}
                {/* Solo admin y manager ven Reportes */}
                {(currentUser?.role === "supervisor" ||
                  currentUser?.role === "store_manager") && (
                  <MobileNavLink
                    href="/shop/reports"
                    icon={<BarChart3 className="h-5 w-5" />}
                    text="Reportes"
                    onClick={handleNavigation}
                  />
                )}

                {/* ========== NUEVO ENLACE A DEVOLUCIONES (MÓVIL) ========== */}
                {(currentUser?.role === "supervisor" ||
                  currentUser?.role === "store_manager") && (
                  <MobileNavLink
                    href="/shop/returns"
                    icon={<RefreshCw className="h-5 w-5" />}
                    text="Devoluciones"
                    onClick={handleNavigation}
                  />
                )}

                {/* Solo admin ve Configuración */}
                {currentUser?.role === "supervisor" && (
                  <MobileNavLink
                    href="/shop/settings"
                    icon={<Settings className="h-5 w-5" />}
                    text="Configuración"
                    onClick={handleNavigation}
                  />
                )}
                {/* Botón Volver a Modo Empresa - Solo para owners */}
                {currentUser?.id?.startsWith("shop_") && (
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-3 h-12 mb-2"
                    onClick={() => {
                      clearCurrentShop();
                      clearCurrentUser();
                      setOpen(false);
                      window.location.href = "/owner/dashboard";
                    }}
                  >
                    <Building className="h-5 w-5" />
                    <span className="text-base">Volver a Modo Empresa</span>
                  </Button>
                )}
                {currentUser?.role === "supervisor" && !currentUser?.id?.startsWith("shop_") && (
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-3 h-12 mb-2"
                    onClick={() => {
                      clearCurrentShop();
                      setOpen(false);
                      window.location.href = "/supervisor/dashboard";
                    }}
                  >
                    <Building className="h-5 w-5" />
                    <span className="text-base">Panel Supervisor</span>
                  </Button>
                )}
                <div className="border-t my-3 pt-3">
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 h-12 text-destructive hover:text-destructive"
                    onClick={() => {
                      handleLogout();
                      setOpen(false);
                    }}
                  >
                    <LogOut className="h-5 w-5" />
                    <span className="text-base">Cerrar Sesión</span>
                  </Button>
                </div>
              </>
            ) : (
              // Usuario no logueado
              <>
                <MobileNavLink
                  href="/home"
                  icon={<Home className="h-5 w-5" />}
                  text="Inicio"
                  onClick={handleNavigation}
                />
                <MobileNavLink
                  href="/shop/login"
                  icon={<User className="h-5 w-5" />}
                  text="Ingresar"
                  onClick={handleNavigation}
                />
                <div className="mt-2">
                  {" "}
                  {/* Cambiado de mt-4 a mt-2 */}
                  <Link href="/admin/login"></Link>
                </div>
                <div className="mt-2">
                  {" "}
                  {/* Agregar espacio entre botones */}
                  <Link href="/register">
                    <Button
                      variant="default"
                      className="w-full justify-start gap-3 h-12"
                      onClick={handleNavigation}
                    >
                      <Store className="h-5 w-5" />
                      <span className="text-base font-bold">Soy Vendedor</span>
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </nav>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Componente para enlaces móviles
function MobileNavLink({
  href,
  icon,
  text,
  onClick,
}: {
  href: string;
  icon: React.ReactNode;
  text: string;
  onClick?: () => void;
}) {
  return (
    <Link href={href}>
      <Button
        variant="ghost"
        className="w-full justify-start gap-3 h-12"
        onClick={onClick}
      >
        {icon}
        <span className="text-base">{text}</span>
      </Button>
    </Link>
  );
}
